/**
 * value(node) can be plain/nested object, array of string, array of array, array of number, array of mixed type i.e. object, string, null, boolean, primitive value.
 *  RulesBook:
 *   - Cases when node is not allowed to select:
 *     1: when node is array of array
 *     2: when node has primitive value (means just 99 , 'string', true can't be selected)
 *   - Cases when node is allowed to select:
 *    When selected node is
 *    1: Plain Object: uses all key value pairs and forms a table of single row
 *    2: Nested Object: uses all key value pairs where values are primitive and forms a table of single row
 *    3: Array of Objects: uses each element/Object as a unique row where keys of object are columns and forms a table with multiple rows.
 *    4: Array of Primitive of same type: table contains a single column having all the elements of array.
 *    5: Array of Mixed Type: table contains only first primitive element
 */

// __________________________________Reusable validation functions starts from here_______________________________

/**
 * Reserved name for the root of the API response. (No table is ever assigned).
 */
export const RESERVED_ROOT_TABLE_NAME = 'web_table';

/**
 * Checks whether value is primitive or not
 * @returns boolean value
 */
export const isPrimitive = (value: any) => {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
};

/**
 * Checks whether value/node is an array or not
 * @returns  boolean value
 */
export const isArray = (value: any) => {
  return Array.isArray(value);
};

/**
 * checks whether value/node is an object or not (plain/nested)
 * @returns boolean value
 */
export const isObject = (value: any) => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/**
 * checks whether an object has at least one primitive-valued key.
 * @returns boolean value
 */
export const hasPrimitiveFields = (value: any) => {
  return isObject(value) && getPrimitiveEntries(value).length > 0;
};

/**
 * It checks whether a array is valid for table
 * @returns boolean value
 * valid array is a plain array(not an array of array, non-empty)
 */
export const isValidArrayForTable = (value: any) => {
  if (!isArray(value) || value.length === 0) return false;
  return getDisplayableItems(value) !== null;
};

/**
 * @param value (primitive)
 * @returns the type of a primitive value
 */
export const getPrimitiveType = (value: any): string => {
  if (value === null) return 'null';
  if (typeof value === 'string') return 'string';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'decimal';
  return 'unknown';
};

/**
 *
 * @returns entries of an object whose values are primitive (skips nested objects)
 */
export const getPrimitiveEntries = (obj: any) => {
  return Object.entries(obj).filter(([, value]) => isPrimitive(value));
};

/**
 * @param array (can be a plain or mixed type)
 * @returns the items from an array that can be displayed as table rows
 *    - array of objects with primitive fields -> all objects
 *    - Mixed array (objects + primitives) or pure primitives of mixed types
 *       -in case of object + primitives(same type) it gives only primitives and discards object
 *       -in case of object + primitives(different type) it gives first primitive and discards others
 *
 */
export const getDisplayableItems = (array: any[]) => {
  if (!array || array.length === 0) return null;

  // Reject arrays containing nested arrays or other non-primitive/non-object values(ex array of array)
  const hasOtherTypes = array.some((element) => !isPrimitive(element) && !isObject(element));
  if (hasOtherTypes) return null;

  const primitives = array.filter(isPrimitive);
  const objects = array.filter(isObject);

  // Array of objects
  if (objects.length === array.length) {
    return array.every(hasPrimitiveFields) ? array : null;
  }

  // has at least some primitives (with or without mixed-in objects)
  if (primitives.length > 0) {
    const types = primitives.map(getPrimitiveType);
    const uniqueTypes = new Set(types);
    if (uniqueTypes.size === 1) {
      // All same type → return all primitives
      return primitives;
    }
    // Mixed strict types → return only the first primitive
    return [primitives[0]];
  }
  return null;
};

/**
 * @returns true when the node can be rendered as a table:
 *  1- Primitive(here mainly checks for object value) -> 1 column & 1 row table
 *  2- Object with at least one primitive field -> columns are primitive keys, 1 row
 *  3- Array of primitives -> multi-row table (single column)
 *  4- Array of Object -> multi row and multi column table(depends on array length)
 */
export const canRenderTable = (value: any) => {
  if (isPrimitive(value)) return true;
  if (hasPrimitiveFields(value)) return true;
  if (isValidArrayForTable(value)) return true;
  return false;
};

// ______________________________ table name and column naming convention_____________________________________//

/**
 * Walks the path from the end (skipping the last `skipCount` segments) and
 * returns the first string key found. Falls back to RESERVED_ROOT_TABLE_NAME
 */
const nearestNamedKey = (path: (string | number)[], skipLast = 0): string => {
  for (let i = path.length - 1 - skipLast; i >= 0; i--) {
    if (typeof path[i] === 'string') {
      return String(path[i]);
    }
  }
  return RESERVED_ROOT_TABLE_NAME;
};

/**
 *  @returns the column name
 */
export const getColumnName = (path: (string | number)[]) => {
  return nearestNamedKey(path, 0);
};

/**
 * Returns the base display name for a node, based purely on its position in the JSON tree — without any uniqueness logic.
 *
 * Rules:
 *  - Primitive value  → nearest named ancestor key (i.e. the parent's key).
 *      ["users", 0, "firstName"]  → "users"   (skips own key + numeric index)
 *      ["current_weather", "temp"] → "current_weather"
 *  - Object or Array  → the node's own key name.
 *      ["users", 27, "hair"]      → "hair"
 *      ["users"]                  → "users"
 */
export const getTableName = (path: (string | number)[], value: any): string => {
  if (isPrimitive(value)) {
    // Skip the node's own key and find the nearest named ancestor.
    return nearestNamedKey(path, 1);
  }
  // For primitive arrays, use the nearest named ancestor (skipping own key), same as primitive values.
  if (isArray(value) && (value as unknown[]).every(isPrimitive)) {
    return nearestNamedKey(path, 1);
  }
  // For objects and other arrays, use the node's own key.
  return nearestNamedKey(path, 0);
};

/**
 *
 * @param baseName  (table name from getTableName without uniqueness logic)
 * @param existingNames (table names on canvas)
 * @returns a unique name for table
 */
export const getUniqueTableName = (baseName: string, existingNames: Set<string>): string => {
  if (!existingNames.has(baseName)) return baseName;

  let counter = 1;
  while (existingNames.has(`${baseName}_${counter}`)) {
    counter++;
  }
  return `${baseName}_${counter}`;
};
